import React, { useState, useEffect, useCallback, useRef } from "react";
import { useParams } from "react-router-dom";
import SuiBox from "components/SuiBox";
import SuiButton from "components/SuiButton";
import Button from "@mui/material/Button";
import Table from "examples/Tables/Table";
import NoData from "containers/DataNotAvailBox";
import { generatePDFDocument } from "utils/pdf";
import { TailSpin } from "react-loader-spinner";
import { dietChartRow } from "./DietRow/rows";
import SuiTypography from "components/SuiTypography";
import { getClientDietChart } from "services/dietchart";
import { toast } from "react-toastify";
import Logomain from "assets/images/logo.png";
import { useSelector, useDispatch } from "react-redux";
import { userSelector } from "store/features/authSlice";
import { getDietChartSettings } from "store/features/authSlice";
import { getDietChartConfiguration } from "store/features/authSlice";
import { dietChartSelector } from "store/features/dietchart";
import DietChartPreviewer from "./components/DietChartPreviewer";
import { doc, getDoc } from "firebase/firestore";
import { firestore } from "firebaseConfig";

const clientPortalPdfOptions = { clientPortalPdf: true };

function DietChart({ title, clientId, selectedClient, adminLogo }) {
  const dispatch = useDispatch();
  const { adminId } = useParams();
  const { dietChartSettings, dietChartConfiguration } =
    useSelector(userSelector);
  const [uid, setUid] = useState(null);
  const { schedules: dietMealTimings } = useSelector(dietChartSelector);

  const [previewLoading, setPreviewLoading] = useState(false);
  const previewRef = useRef();

  useEffect(() => {
    setUid(adminId || null);
  }, [adminId]);

  useEffect(() => {
    if (uid) {
      dispatch(getDietChartSettings({ uid }));
    }
  }, [dispatch, uid]);
  useEffect(() => {
    if (uid) {
      dispatch(getDietChartConfiguration({ uid }));
    }
  }, [dispatch, uid]);
  const [dietchart, setDietChart] = useState([]);
  const [adminDetails, setAdminDetails] = useState(null);

  const getSelectedClientDietChart = useCallback(async () => {
    try {
      if (clientId) {
        setLoader(true);
        const dietchart_ = await getClientDietChart(clientId);
        setDietChart(dietchart_);
        setLoader(false);
      }
    } catch (e) {
      setLoader(false);
      toast.info("Something went wrong!!!");
    }
  }, [clientId]);

  useEffect(() => {
    if (clientId) {
      getSelectedClientDietChart();
    }
  }, [clientId, getSelectedClientDietChart]);

  useEffect(() => {
    const fetchAdminDetails = async () => {
      try {
        if (!uid) {
          setAdminDetails(null);
          return;
        }
        const snapshot = await getDoc(doc(firestore, "profile", uid));
        setAdminDetails(snapshot.exists() ? { ...snapshot.data(), uid } : null);
      } catch {
        setAdminDetails(null);
      }
    };
    fetchAdminDetails();
  }, [uid]);

  const [rows, setRows] = useState([]); // rows for recipe table
  const [loader, setLoader] = useState(false);
  const [dietChartArrayv1, setDietChartArrayv1] = useState([]);

  useEffect(() => {
    const approvedAndFiltered = dietchart.filter((item) => {
      const isApproved = !dietChartConfiguration || item.isApproved;

      if (!dietChartSettings?.isDietChartExpiryEnabled) {
        return isApproved;
      }

      const toDate = new Date(item.to);
      const today = new Date();
      today.setHours(0, 0, 0, 0);

      return isApproved && toDate >= today;
    });

    setDietChartArrayv1(approvedAndFiltered);
  }, [dietchart, dietChartConfiguration, dietChartSettings]);

  const logo = adminLogo ?? Logomain;
  const [chartArray, setChartArray] = useState([]);

  const handlePreview = async () => {
    try {
      if (!adminDetails) {
        toast.error("Admin details are still loading. Please try again.");
        return;
      }
      setPreviewLoading(true);
      const url = await generatePDFDocument(
        dietChartSettings,
        chartArray,
        selectedClient,
        logo,
        setLoader,
        false,
        dietMealTimings,
        true,
        adminDetails,
        clientPortalPdfOptions
      );
      previewRef.current?.openPreview(url);
    } catch (error) {
      toast.error("Failed to generate preview");
    } finally {
      setPreviewLoading(false);
    }
  };

  const columns = [
    {
      name: "View",
      align: "left",
      minWidth: "190px",
      component: (
        <SuiBox
          style={{
            display: "flex",
            alignItems: "center",
            position: "relative",
            opacity: 1,
          }}
        >
          {chartArray.length > 0 ? (
            <p
              style={{
                fontSize: "0.625rem",
                fontWeight: 700,
                color: "#17c1e8",
                cursor: "pointer",
              }}
              onClick={() => {
                if (!adminDetails) {
                  toast.error("Admin details are still loading. Please try again.");
                  return;
                }
                generatePDFDocument(
                  dietChartSettings,
                  chartArray,
                  selectedClient,
                  logo,
                  setLoader,
                  1,
                  dietMealTimings,
                  false,
                  adminDetails,
                  clientPortalPdfOptions
                );
              }}
            >
              DOWNLOAD NOW
            </p>
          ) : (
            <p
              style={{
                color: "#8392ab",
                fontSize: "0.625rem",
                fontWeight: 700,
                cursor: "not-allowed",
                display: "flex",
              }}
            >
              DOWNLOAD NOW
              {loader && <TailSpin color="#17c1e8" height={20} width={20} />}
            </p>
          )}
        </SuiBox>
      ),
    },
    { name: "Date/Time (Added)", align: "left", minWidth: "200px" },
    { name: "Week Plan", align: "left", minWidth: "100px" },
    { name: "Chart Type", align: "left", minWidth: "100px" },
    { name: "Diet Date for Client", align: "left", minWidth: "200px" },
    { name: "Early Morning", align: "left", minWidth: "200px" },
    { name: "Breakfast", align: "left", minWidth: "200px" },
    { name: "Mid Morning", align: "left", minWidth: "200px" },
    { name: "Brunch", align: "left", minWidth: "200px" },
    { name: "Mid Meal", align: "left", minWidth: "200px" },
    { name: "Lunch", align: "left", minWidth: "200px" },
    { name: "Post Lunch", align: "left", minWidth: "200px" },
    { name: "Evening", align: "left", minWidth: "200px" },
    { name: "Before Dinner", align: "left", minWidth: "200px" },
    { name: "Dinner", align: "left", minWidth: "200px" },
    { name: "Post Dinner", align: "left", minWidth: "200px" },
    { name: "Late Night", align: "left", minWidth: "200px" },
    { name: "Notes", align: "left", minWidth: "200px" },
    { name: "Calories", align: "left", minWidth: "120px" },
  ];

  const selectHandler = useCallback((item, value) => {
    setDietChartArrayv1((prev) =>
      prev.map((chart) =>
        chart.dietChartId === item.dietChartId
          ? { ...chart, selected: value }
          : chart
      )
    );
  }, []);

  useEffect(() => {
    setRows(dietChartRow(dietChartArrayv1 ?? [], selectHandler));
  }, [dietChartArrayv1, selectHandler]);

  useEffect(() => {
    const selectedCharts = (dietChartArrayv1 ?? [])
      .filter((item) => item?.selected)
      .map((item) => ({ dietChart: item }));
    setChartArray(selectedCharts);
  }, [dietChartArrayv1]);

  return (
    <>
      <SuiBox overflow="hidden" minWidth="250px">
        <SuiBox
          display="flex"
          justifyContent="space-between"
          alignItems="center"
          mb={2}
        >
          <SuiBox>
            <SuiTypography variant="h5" style={{ color: "#17C1E8" }}>
              {title}
            </SuiTypography>
          </SuiBox>
        </SuiBox>

        <SuiBox
          sx={{
            "& .MuiTableRow-root:not(:last-child)": {
              "& td": {
                borderBottom: ({ borders: { borderWidth, borderColor } }) =>
                  `${borderWidth[1]} solid ${borderColor}`,
              },
            },
          }}
        >
          {rows.length ? (
            <Table columns={columns} rows={rows} maxRows={5} />
          ) : (
            <NoData />
          )}
        </SuiBox>

        {chartArray.length > 0 ? (
          <div
            style={{
              position: "sticky",
              marginTop: 100,
              width: "100%",
              backgroundColor: "transparent",
              display: "flex",
              justifyContent: "end",
              bottom: "2.6rem",
            }}
          >
            <SuiButton
              component={Button}
              color="dark"
              variant="gradient"
              onClick={handlePreview}
              disabled={previewLoading}
            >
              {previewLoading ? (
                <TailSpin height={20} width={20} color="#ffffff" />
              ) : (
                "PREVIEW NOW"
              )}
            </SuiButton>
          </div>
        ) : null}
      </SuiBox>
      <DietChartPreviewer ref={previewRef} />
    </>
  );
}

export default DietChart;
